import type { WorkflowTrackers } from '../schema/WorkflowTrackers';
import type { WorkflowTrackingConfig } from '../schema/WorkflowTrackingConfig';

// list of modules registered for workflow tracking
const workflowTrackers = new Set<WorkflowTrackers>();

// workflows being tracked
let workflows = [];

/**
 * Use this util to declare or add the workflows for which performance and reliability metrics must be tracked.
 */
export function trackWorkflow(entries: WorkflowTrackingConfig[], tracker?: WorkflowTrackers) {
    if (tracker && workflowTrackers.has(tracker)) {
        return;
    }

    const allEntries = [...workflows, ...entries];
    validate(allEntries);
    workflows = allEntries;

    workflowTrackers.add(tracker);
}

/**
 * Resets the registers workflows
 */
export function clearWorkflows() {
    workflows = [];

    workflowTrackers.clear();
}

/**
 * Check if any workflows are registered
 */
export function areWorkflowsPresent() {
    return workflows.length > 0;
}

/**
 * Is this signal a registered workflow start signal
 */
export function isStartSignal(signalName: string) {
    return workflows.some(workflow => workflow.start === signalName);
}

/**
 * Is this signal a registered workflow end signal
 */
export function isEndSignal(signalName: string) {
    return workflows.some(workflow => workflow.end?.includes(signalName));
}

/**
 * Is this signal a registered workflow checkmark signal
 */
export function isCheckmarkSignal(signalName: string) {
    return workflows.some(workflow => workflow.checkmarks?.includes(signalName));
}

/**
 * Get workflows given the checkmark signal
 */
export function getWorkflowsForCheckmarkSignal(signalName: string) {
    return workflows.filter(workflow => workflow.checkmarks?.includes(signalName));
}

/**
 * Get workflows given the end signal
 */
export function getWorkflowsForEndSignal(signalName: string) {
    return workflows.filter(workflow => workflow.end?.includes(signalName));
}

/**
 * Get the workflow name
 */
export function getWorkflowName(startSignal: string) {
    const entry = workflows.find(workflow => workflow.start === startSignal);
    return entry && entry.name;
}

/**
 * Is the workflow having multiple branches
 */
export function isWorkflowWithMultipleBranches(workflow: WorkflowTrackingConfig) {
    return workflow.end.length > 1;
}

/**
 * Identify the workflow given the start signal and return the corresponding options
 */
export function getOptions(startSignal: string) {
    const entry = workflows.find(workflow => workflow.start === startSignal);
    return entry && entry.options;
}

/**
 * Utility to check if the workflow is an internal one or user action driven one
 */
export function isWorkflowInternal(startSignal: string) {
    const entry = workflows.find(workflow => workflow.start === startSignal);
    return !!entry?.isInternal;
}

/**
 * Internal function to validate the declared workflows
 * Currently the validation logic checks if the start signals are unique
 */
function validate(entries: WorkflowTrackingConfig[]) {
    const startSignals = entries.map(workflow => workflow.start);
    if (new Set(startSignals).size !== startSignals.length) {
        throw new Error('Workflow start signals must be unique');
    }
}
